import React from 'react';

import {
  getLocalZebraDevices,
  ZebraBrowserPrintException,
  ZebraDevice,
} from 'label-printing/ZebraBrowserPrint';
import SelectDropdown, {closeSelectDropdown} from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

export interface ZebraBrowserPrinterSelectorProps {
  onChange: (device: ZebraDevice) => void;
  device: ZebraDevice | null;
}

interface State {
  loadedDevices: Array<ZebraDevice> | null;
  hasError: boolean;
}

class ZebraBrowserPrinterSelector extends React.Component<ZebraBrowserPrinterSelectorProps> {
  override state: State = {
    loadedDevices: null,
    hasError: false,
  };
  dropdown: SelectDropdown | null = null;

  override async componentDidMount(): Promise<void> {
    try {
      this.setState({loadedDevices: await getLocalZebraDevices()});
    } catch (e) {
      if (e instanceof ZebraBrowserPrintException) {
        this.setState({loadedDevices: [], hasError: true});
      } else {
        throw e;
      }
    }
  }

  private handleOnChange = (value: OptionValue): void => {
    if (this.state.loadedDevices && value) {
      this.props.onChange(this.state.loadedDevices.find((device) => device.id === value)!);
    }
    if (this.dropdown) {
      closeSelectDropdown(this.dropdown);
    }
  };

  private getPrinterDropdownOptions(): Array<OptionData> {
    if (!this.state.loadedDevices) {
      return [];
    }
    if (this.state.loadedDevices.length === 0) {
      return [{name: 'No printers found'}];
    }
    return this.state.loadedDevices.map((printer) => ({
      name: printer.name,
      value: printer.id,
    }));
  }

  override render(): JSX.Element {
    if (!this.state.loadedDevices) {
      return <SelectDropdown menu={<Menu />} disabled={true} displayName="Loading printers..." />;
    }
    if (this.state.hasError) {
      return (
        <span>
          <BenchlingIcon icon="error" className="u-tiny-spacing-right" />
          Error searching for printers. Make sure the Zebra Browser Print app is running.
        </span>
      );
    } else if (this.state.loadedDevices.length === 0) {
      return (
        <span>
          <BenchlingIcon icon="error" className="u-tiny-spacing-right" />
          No printers found. Make sure the Zebra Browser Print app can find the printer.
        </span>
      );
    }

    return (
      <SelectDropdown
        menu={
          <SearchableMenu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            onSelect={this.handleOnChange}
            optionDatas={this.getPrinterDropdownOptions()}
          />
        }
        displayName={this.props.device ? this.props.device.name : 'Select a printer...'}
        ref={(node) => {
          this.dropdown = node;
        }}
      />
    );
  }
}

export default ZebraBrowserPrinterSelector;
